import React, { createContext, lazy, Suspense, useEffect, useMemo, useReducer, useState } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";

import Tree from "./pages/Tree";
import { appReducer, AppState, initialAppState } from "./services/state";
import Loading from "./pages/Loading";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { doNotRetryOn4xx } from "./services/api";
import { getCurrentAdmin } from "./services/auth";

type AppContextValues = {
  appState: AppState;
  appDispatch: any;
};

const Commitment = lazy(() => import("./pages/Donation"));
const Admin = lazy(() => import("./pages/Admin"));
const Login = lazy(() => import("./pages/Login"));
const Success = lazy(() => import("./pages/Success"));
const PickAnother = lazy(() => import("./pages/PickAnother"));
export const AppContext = createContext<AppContextValues>({ appState: null, appDispatch: null });

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
      retry: doNotRetryOn4xx,
    },
  },
});

const ProtectedAdminRoute = () => {
  const [authStatus, setAuthStatus] = useState<"checking" | "authenticated" | "unauthenticated">("checking");

  useEffect(() => {
    getCurrentAdmin().then(user => {
      setAuthStatus(user ? "authenticated" : "unauthenticated");
    });
  }, []);

  if (authStatus === "checking") {
    return <Loading />;
  }
  if (authStatus === "unauthenticated") {
    return <Navigate to="/login" replace />;
  }
  return <Admin />;
};

function App() {
  // app state setup
  const [appState, appDispatch] = useReducer(appReducer, initialAppState);
  const appContextValue = useMemo(() => ({ appState, appDispatch }), [appState]);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route element={<AppContextRoute value={appContextValue} />}>
              <Route path="/" element={<Tree />} />
              <Route path="donate/:childId/:itemType" element={<Commitment />} />
              <Route path="success" element={<Success />} />
              <Route path="pick-another" element={<PickAnother />} />
              <Route path="admin" element={<ProtectedAdminRoute />} />
              <Route path="login" element={<Login />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

const AppContextRoute = ({ value }) => (
  <AppContext.Provider value={value}>
    <Outlet />
  </AppContext.Provider>
);

export default App;
